import { useState, useEffect, useCallback } from "react";
import { message } from "antd";
// import { getRequest, postRequest, putRequest } from "../util/service";
import { useSelector } from "react-redux";
import { getRequest, postRequest, putRequest } from "./../../util/service";
import { fileNameError, isValidFileName } from "../../util/fileNameValidation";

export const useFragmentation = (projectId, active) => {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitModalVisible, setSubmitModalVisible] = useState(false);
  const [isViewModalVisible, setViewModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const { userName, userEmail } = useSelector(state => state.header);
  const [isSuccessModalVisible, setSuccessModalVisible] = useState(false);
  const [successData, setSuccessData] = useState(null);

  const [existingSmiFiles, setExistingSmiFiles] = useState([]);
  const [smiFilesLoading, setSmiFilesLoading] = useState(false);

   const fetchRows = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const resp = await getRequest(`/riva/fragmenter/${projectId}`);
      console.log("resp --->", resp);
      const data = Array.isArray(resp?.data) ? resp.data : [];
      const mappedData = data.map((item) => ({
        id: item.f_record_id,
        projectId: item.p_record_id,
        inputFile: item.f_input_json?.fragmenter_file,
        description: item.f_input_json?.description || "",
        status: item.status,
        submittedBy: item.submitted_id,
        submittedTimestamp: item.submitted_ts,
        completedTimestamp: item.completed_ts,
        statusMessage: item.status_message,
        outputFile: item.output_paths?.result_file,
        inputParams: item.f_input_json,
      }));
       mappedData.sort((a, b) => new Date(b.submittedTimestamp) - new Date(a.submittedTimestamp));
      setRows(mappedData);
    } catch (err) {
      message.error(err?.message || "Failed to load fragmentation jobs");
    } finally {
      setLoading(false);
    }
  }, [projectId]);
  useEffect(() => { if (active) fetchRows(); }, [active, fetchRows]);

  useEffect(() => {
    if (isSubmitModalVisible && projectId) {
      setSmiFilesLoading(true);
      getRequest(`/riva/moleculegenbatches/${projectId}/Fragmenterfiles`)
        .then(resp => {
            const files = resp.data?.[0]?.files || [];
         setExistingSmiFiles(files.filter(f => f).map(f => ({ label: f, value: f })));
        })
        .catch(() => message.error("Could not load existing SMI files."))
        .finally(() => setSmiFilesLoading(false));
    }
  }, [isSubmitModalVisible, projectId]);

  const handleDownloadInputFile = async (record) => {
    const key = `frag-input-dl-${record.id}`;
    try {
        message.loading({ content: `Preparing download for ${record.inputFile}...`, key });
        const payload = {
            file_type: "Fragmenterfiles",
            file_name: record.inputFile,
            fragmenter_id: record.id,
            action: "download",
        };
        const resp = await postRequest(`/riva/moleculegenbatches/${projectId}/presigned-url`, payload);
        const url = resp?.data?.url;
        if (!url) throw new Error("Could not get download link.");
        
        const fileResponse = await fetch(url);
        if (!fileResponse.ok) throw new Error(`File download failed: ${fileResponse.statusText}`);
        const blob = await fileResponse.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = blobUrl;
        a.download = record.inputFile;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(blobUrl);

        message.success({ content: "Download started!", key, duration: 2 });
    } catch (err) {
        message.error({ content: err.message || "Download failed", key });
    }
  };

  const fetchSmiFiles = async () => {
    if (!projectId) return;
    setSmiFilesLoading(true);
    try {
      const resp = await getRequest(`/riva/moleculegenbatches/${projectId}/Fragmenterfiles`);
      // The response has a nested structure, we need to extract the 'files' array
      const files = resp.data?.[0]?.files || [];
      setExistingSmiFiles(files.map(f => ({ label: f, value: f })));
    } catch (err) {
      message.error("Could not load existing SMI files.");
      setExistingSmiFiles([]); // Ensure it's an empty array on failure
    } finally {
      setSmiFilesLoading(false);
    }
  };

  const handleDownloadOutputFile = async (record) => {
    const key = `frag-dl-${record.id}`;
    try {
      message.loading({ content: "Preparing download…", key });
      const payload = {
        file_type: "FragmenterOutfiles",
        file_name: record.outputFile,
        fragmenter_id: record.id,
        action: "download",
      };
      const resp = await postRequest(`/riva/moleculegenbatches/${projectId}/presigned-url`, payload);
      const url = resp?.data?.url;
      if (!url) throw new Error(resp?.data?.message || "No URL returned");

      const fileResponse = await fetch(url);
      if (!fileResponse.ok) throw new Error(`Failed to fetch file: ${fileResponse.statusText}`);
      
      const blob = await fileResponse.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = record.outputFile;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);

      message.success({ content: "Download started", key, duration: 2 });
    } catch (err) {
      message.error({ content: err?.message || "Download failed", key });
    }
  };

  const handleUpload = async (file) => {
    const key = `upload-${Date.now()}`;
    if (!isValidFileName(file.name)) {
      message.error(fileNameError(file.name));
      return null;
    }
    message.loading({ content: `Uploading ${file.name}...`, key });
    try {
      const presignedPayload = { file_type: "Fragmenterfiles", file_name: file.name, action: "upload" };
      const presignedResp = await postRequest(`/riva/moleculegenbatches/${projectId}/presigned-url`, presignedPayload);
      const { url: uploadUrl, key: fileKey } = presignedResp.data;

      if (!uploadUrl) throw new Error("Failed to get an upload URL.");
      
      await putRequest(uploadUrl, file, { "Content-Type": file.type || "text/plain" });
      message.success({ content: `${file.name} uploaded successfully!`, key });
      
      // ✅ FIX: Refresh the list of files after a successful upload
      await fetchSmiFiles();
      
      return fileKey;
    } catch (err) {
      message.error({ content: err.message || `Failed to upload ${file.name}`, key });
      return null;
    }
  };
  
  const handleSubmit = async (form, uploadedFile) => {
    try {
      const values = await form.validateFields();
      setIsSubmitting(true);
      let fragmenterFileKey = values.fragmenter_file;

      if (uploadedFile && uploadedFile.name === fragmenterFileKey) {
        const presignedPayload = { file_type: "Fragmenterfiles", file_name: uploadedFile.name, action: "upload" };
        const presignedResp = await postRequest(`/riva/moleculegenbatches/${projectId}/presigned-url`, presignedPayload);
        const { url: uploadUrl, key } = presignedResp.data;
        if (!uploadUrl) throw new Error("Failed to get an upload URL.");

        await putRequest(uploadUrl, uploadedFile, { "Content-Type": "application/zip" });
        fragmenterFileKey = key;
      }
      
      if (!fragmenterFileKey || fragmenterFileKey === "--upload-new--") {
        throw new Error("Input SMI file is not specified.");
      }

      const rxnSMARTSValue = values.rxnSMARTS_select === "brics" 
        ? "BRICS" 
        : values.rxnSMARTS_custom;
      // ✅ FINAL PAYLOAD: Constructs the exact payload for the submit API
      const payload = {
        modelObj: {
         fragmenter_file: values.fragmenter_file,
          rxnSMARTS: rxnSMARTSValue,
          flattenChirality: values.flattenChirality,
          description: values.description || "",
          frag_output_file: values.output_file_tag ? `${values.output_file_tag}.json` : undefined,
          frag_threshold: values.rel_pop_threshold,
        },
        userDetail: { userName, userEmail, projectId }
      };
      
      const submitResp = await postRequest("/riva/fragmenter", payload);

      // message.success(submitResp?.data?.message || "Fragmentation job submitted successfully!");
      setSuccessData(submitResp.data);
      setSubmitModalVisible(false); // Close the submission form
      setSuccessModalVisible(true); // Open the success summary
      setSubmitModalVisible(false);
      fetchRows();
    } catch (err) {
      if (!err?.errorFields) message.error(err?.message || "Submission failed");
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    loading, rows, isSubmitting, isSubmitModalVisible, setSubmitModalVisible,
    isViewModalVisible, setViewModalVisible, selectedRecord, setSelectedRecord,
    fetchRows, handleDownloadInputFile, handleDownloadOutputFile, handleSubmit, 
    existingSmiFiles, smiFilesLoading, isSuccessModalVisible, setSuccessModalVisible, successData,handleUpload
  };
};
